import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  usePatchProjectMutation,
  usePostTaskMutation,
} from '@greenkub-fronts/data-access';
import {
  AppDispatch,
  ArraySignatures,
  FixMe,
  RootState,
} from '@greenkub-fronts/shared/interface';
import { addSnackMessage } from '@greenkub-fronts/domain/snackbar';
import {
  onCustomerSign,
  openModalSignature,
  setHeaderFocus,
  setSignatures,
} from '@greenkub-fronts/domain/signature';

import HeaderFooterLandingPage from '../../ui/web/header-footer-landing-page/header-footer-landing-page';
import { TemplateEnum } from '../../utils/enum';

export interface HeaderFooterLandingPageFeatureProps {
  sign1Ref: FixMe;
  sign2Ref?: FixMe;
  isBDC?: boolean;
}

// Bannière pour signer ou partager le lien
const HeaderFooterLandingPageFeature = (
  props: HeaderFooterLandingPageFeatureProps
) => {
  const {
    sign1Ref,
    sign2Ref = null, // Si bdc ou upsell
    isBDC = false,
  } = props;

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const {
    projectId,
    project,
    isSignature,
    document,
    projectToken,
    fromExisting,
    signatures,
    upsell,
    headerFocus,
    documentSlug,
    upsellId,
  } = useSelector((state: RootState) => state.signature);

  const [patchProject] = usePatchProjectMutation();
  const [postTask] = usePostTaskMutation();

  const [checkBoxPendingSign, setCheckBoxPendingSign] =
    useState<boolean>(false);

  /**
   * Checkbox raccord avec les données du projet
   */
  useEffect(() => {
    switch (documentSlug) {
      case TemplateEnum.MANDAT_GESTION:
        if (project?.mandatGestionSignPending) {
          setCheckBoxPendingSign(true);
        }
        break;
      case TemplateEnum.DECHARGE_NON_CONFORMITE:
        if (project?.nonConformityDisChargeSignPending) {
          setCheckBoxPendingSign(true);
        }
        break;
      case TemplateEnum.DECHARGE_TVX_SANS_DECLARATION:
        if (project?.workWithoutDeclarationDischargeSignPending) {
          setCheckBoxPendingSign(true);
        }
        break;
    }
  }, [project, documentSlug]);

  /**
   * Scroll to signature position
   */
  const goToSign = () => {
    if (fromExisting) {
      // Open modal
      dispatch(openModalSignature({ currentIndexSignature: 0 }));
      return;
    }

    const currentSignatures: ArraySignatures = [...signatures];

    if (signatures[0] !== true) {
      sign1Ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
      currentSignatures[0] = 'pulse';
    } else {
      sign2Ref?.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
      currentSignatures[1] = 'pulse';
    }

    dispatch(setHeaderFocus({ headerFocus: false }));
    dispatch(setSignatures(currentSignatures));
  };

  const allSignatureDone = useMemo(() => {
    let allTrue = true;
    signatures.forEach((sign) => {
      if (sign !== true) {
        allTrue = false;
      }
    });
    return allTrue;
  }, [signatures]);

  const copyQuoteLink = async () => {
    const link = `${window.location.origin}/devis/${project?.token}`;
    try {
      await navigator.clipboard.writeText(link);
      dispatch(addSnackMessage('positive', 'Le lien a bien été copié'));
    } catch (_) {
      dispatch(addSnackMessage('negative', 'Impossible de copier le lien'));
    }
  };

  const copySignLink = async () => {
    let link = '';
    if (fromExisting) {
      link = `${window.location.origin}/signature/sign/${project?.token}/document/${document?.id}`;
    } else {
      if (upsellId) {
        link = `${window.location.origin}/signature/sign/${project?.token}/${documentSlug}/${upsell?.token}`;
      } else {
        link = `${window.location.origin}/signature/sign/${project?.token}/${documentSlug}`;
      }
    }

    try {
      await navigator.clipboard.writeText(link);
      dispatch(addSnackMessage('positive', 'Le lien a bien été copié'));
    } catch (_) {
      dispatch(addSnackMessage('negative', 'Impossible de copier le lien'));
    }
  };

  const askForSignHandler = (documentSlug: string) => {
    let result = false;
    if (!project) return;

    switch (documentSlug) {
      case TemplateEnum.MANDAT_GESTION:
        if (!project.mandatGestionSignPending) {
          result = window.confirm(
            "En cochant cette case, vous placer en évidence la demande de signature de ce document dans l'espace client"
          );
        } else {
          result = window.confirm('Retirer la mise en évidence de signature ?');
        }
        break;
      case TemplateEnum.DECHARGE_NON_CONFORMITE:
        if (!project.nonConformityDisChargeSignPending) {
          result = window.confirm(
            "En cochant cette case, vous placer en évidence la demande de signature de ce document dans l'espace client"
          );
        } else {
          result = window.confirm('Retirer la mise en évidence de signature ?');
        }
        break;
      case TemplateEnum.DECHARGE_TVX_SANS_DECLARATION:
        if (!project.workWithoutDeclarationDischargeSignPending) {
          result = window.confirm(
            "En cochant cette case, vous placer en évidence la demande de signature de ce document dans l'espace client"
          );
        } else {
          result = window.confirm('Retirer la mise en évidence de signature ?');
        }
        break;
    }

    if (!result) return;

    switch (documentSlug) {
      case TemplateEnum.MANDAT_GESTION:
        patchProject({
          projectId: projectId,
          project: {
            mandatGestionSignPending: !project.mandatGestionSignPending,
          },
        });
        setCheckBoxPendingSign(!project.mandatGestionSignPending);
        break;
      case TemplateEnum.DECHARGE_NON_CONFORMITE:
        patchProject({
          projectId: projectId,
          project: {
            nonConformityDisChargeSignPending:
              !project.nonConformityDisChargeSignPending,
          },
        });
        setCheckBoxPendingSign(!project.nonConformityDisChargeSignPending);
        break;
      case TemplateEnum.DECHARGE_TVX_SANS_DECLARATION:
        patchProject({
          projectId: projectId,
          project: {
            workWithoutDeclarationDischargeSignPending:
              !project.workWithoutDeclarationDischargeSignPending,
          },
        });
        setCheckBoxPendingSign(
          !project.workWithoutDeclarationDischargeSignPending
        );
        break;
    }
  };

  const onCustomerSignHandler = async () => {
    dispatch(onCustomerSign({})).then((resp: any) => {
      const documentId = document?.id ?? resp?.payload?.documentId ?? '';

      if (resp.type === 'onCustomerSign/fulfilled') {
        navigate(`/signature/sign/success/${projectToken}/${documentId}`);
      }
    });
  };

  const createTaskTrigger = () => {
    postTask({ projectId, slug: 'Compte rendu R' });
  };

  return (
    <HeaderFooterLandingPage
      headerFocus={headerFocus}
      isSignature={isSignature}
      signatures={signatures}
      allSignatureDone={allSignatureDone}
      onCustomerSignHandler={onCustomerSignHandler}
      goToSign={goToSign}
      documentSlug={documentSlug as TemplateEnum}
      checkBoxPendingSign={checkBoxPendingSign}
      askForSignHandler={askForSignHandler}
      project={project}
      copyQuoteLink={copyQuoteLink}
      copySignLink={copySignLink}
      sendEmail={() => console.info('Envoi de mail inactif')}
      navigate={navigate}
      createTaskTrigger={createTaskTrigger}
      isBDC={isBDC}
      signedAt={document?.signedAt}
    />
  );
};

export default HeaderFooterLandingPageFeature;
